import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

const IconDashboard = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-3m0 0l7-4 7 4M5 9v7a1 1 0 001 1h12a1 1 0 001-1V9m-9 3l2 2m4-4l2 2" />
  </svg>
);
const IconThermometer = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const IconGear = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const IconCheckmark = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);
const IconTrendingUp = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const IconAlertTriangle = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4v2m0 4v2m0 6v0M5.5 6l-2.121-2.121M2.379 9.379l-2.121 2.121M19.5 6l2.121-2.121M22.621 9.379l2.121 2.121" />
  </svg>
);
const IconDocument = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const IconDownload = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);
const IconHeadset = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);
const IconLogout = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);
const IconBell = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);
const IconBox = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m0 0v10l8 4" />
  </svg>
);
const IconClock = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22a10 10 0 100-20 10 10 0 000 20z" />
  </svg>
);
const IconUserGroup = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);
const IconTruck = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17h1M15 17h1M3 11V7a1 1 0 011-1h9l3 4h3a1 1 0 011 1v4a1 1 0 01-1 1h-1M3 11h13M3 11v4a1 1 0 001 1h1m0 0a2 2 0 104 0m-4 0a2 2 0 104 0m6 0a2 2 0 104 0 2 2 0 00-4 0" />
  </svg>
);
const IconClipboardList = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);
const IconTempTracking = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);
const IconChefHat = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 18h8M6 12c0-3.5 2-6 6-6s6 2.5 6 6c0 1.8-1.2 3.3-3 4H9c-1.8-.7-3-2.2-3-4z"/>
    <rect x="8" y="16" width="8" height="2.5" rx="1" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const iconMap: Record<string, any> = {
  dashboard: IconDashboard,
  thermometer: IconThermometer,
  equipment: IconGear,
  cleaning: IconCheckmark,
  oil: IconTrendingUp,
  incidents: IconAlertTriangle,
  pms: IconDocument,
  exports: IconDownload,
  support: IconHeadset,
  configuration: IconGear,
  notifications: IconBell,
  logout: IconLogout,
  box: IconBox,
  time: IconClock,
  chefhat: IconChefHat,
  temptracking: IconTempTracking,
  suppliers: IconUserGroup,
  reception: IconTruck,
  tasks: IconClipboardList,
};

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);

  const handleNavClick = () => { onClose?.(); };

  return (
    <>
      <div className="hidden lg:flex lg:w-72 bg-gradient-to-b from-neutral-50 via-white to-neutral-100 text-neutral-800 flex-col fixed left-0 top-0 h-screen border-r border-neutral-200 shadow-lg">
        <SidebarContent location={location} user={user} onNavClick={handleNavClick} />
      </div>
      <div className={`fixed left-0 top-0 h-screen w-72 max-w-full bg-gradient-to-b from-neutral-50 via-white to-neutral-100 text-neutral-800 flex flex-col z-30 transition-transform duration-300 border-r border-neutral-200 shadow-lg lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <SidebarContent location={location} user={user} onNavClick={handleNavClick} />
      </div>
    </>
  );
}

interface NavItem {
  href: string;
  labelKey: string;
  icon: string;
  roles?: string[];
  children?: NavItem[];
}

interface SidebarContentProps {
  location: ReturnType<typeof useLocation>;
  user: any;
  onNavClick?: () => void;
}

function SidebarContent({ location, user, onNavClick }: SidebarContentProps) {
  const logout = useAuthStore((state) => state.logout);
  const { t } = useTranslation();
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const navItems: NavItem[] = [
    { href: '/', labelKey: 'nav.dashboard', icon: 'dashboard' },
    {
      href: '/tasks-group',
      labelKey: 'nav.tasks',
      icon: 'tasks',
      children: [
        { href: '/tasks', labelKey: 'nav.tasksTodo', icon: 'tasks' },
        { href: '/tasks/done', labelKey: 'nav.tasksDone', icon: 'cleaning' },
      ],
    },
    { href: '/temperature-logs', labelKey: 'nav.temperature', icon: 'thermometer' },
    {
      href: '/equipment-group',
      labelKey: 'nav.equipment',
      icon: 'equipment',
      children: [
        { href: '/equipment', labelKey: 'nav.equipmentList', icon: 'equipment' },
        { href: '/equipment/config', labelKey: 'nav.configuration', icon: 'configuration', roles: ['OWNER'] },
      ],
    },
    {
      href: '/cleaning-group',
      labelKey: 'nav.cleaning',
      icon: 'cleaning',
      children: [
        { href: '/cleaning', labelKey: 'nav.cleaningPlan', icon: 'cleaning' },
        { href: '/cleaning/config', labelKey: 'nav.configuration', icon: 'configuration', roles: ['OWNER'] },
      ],
    },
    { href: '/oil', labelKey: 'nav.oil', icon: 'oil' },
    { href: '/temp-tracking', labelKey: 'nav.tempTracking', icon: 'temptracking' },
    { href: '/incidents', labelKey: 'nav.incidents', icon: 'incidents' },
    { href: '/pms', labelKey: 'nav.pms', icon: 'pms' },
    {
      href: '/traceability-group',
      labelKey: 'nav.traceability',
      icon: 'box',
      children: [
        { href: '/traceability', labelKey: 'nav.traceabilityProducts', icon: 'box' },
        { href: '/traceability/preparations', labelKey: 'nav.preparations', icon: 'chefhat' },
      ],
    },
    {
      href: '/reception-group',
      labelKey: 'nav.reception',
      icon: 'reception',
      children: [
        { href: '/reception', labelKey: 'nav.receptions', icon: 'reception' },
        { href: '/suppliers', labelKey: 'nav.suppliers', icon: 'suppliers', roles: ['OWNER'] },
      ],
    },
    { href: '/exports', labelKey: 'nav.exports', icon: 'exports' },
    { href: '/notifications', labelKey: 'nav.notifications', icon: 'notifications' },
    { href: '/support', labelKey: 'nav.support', icon: 'support' },
    { href: '/settings', labelKey: 'nav.settings', icon: 'configuration' },
  ];

  const toggleSubmenu = (href: string) => {
    setExpandedMenus(prev => ({ ...prev, [href]: !prev[href] }));
  };

  return (
    <>
      <div className="px-4 py-0 border-b border-neutral-200 bg-white">
        <div className="flex items-center" style={{ minHeight: '90px' }}>
          <img src="/logo.png" alt="HACCP APP" className="w-40 h-28 object-contain flex-shrink-0" style={{ maxWidth: '100%', maxHeight: '80px' }} />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map((item, idx) => {
          const isActive = location.pathname === item.href;
          const IconComponent = iconMap[item.icon];
          const visibleChildren = item.children?.filter(child => !child.roles || child.roles.includes(user?.role));
          const hasChildren = visibleChildren && visibleChildren.length > 0;
          const isChildActive = hasChildren && visibleChildren!.some(child => location.pathname === child.href);
          const isExpanded = expandedMenus[item.href] || isChildActive;

          return (
            <div key={item.href}>
              <Link
                to={item.href}
                onClick={(e) => {
                  if (hasChildren) { e.preventDefault(); toggleSubmenu(item.href); }
                  else { onNavClick?.(); }
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 font-medium group relative overflow-hidden ${
                  isActive || isChildActive
                    ? 'bg-gradient-to-r from-primary-100 to-primary-50 text-primary-700 shadow-md hover:shadow-lg border-l-4 border-primary-500'
                    : 'text-neutral-700 hover:bg-gradient-to-r hover:from-neutral-100 hover:to-neutral-50 hover:text-brand-dark hover:shadow-md'
                }`}
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                {!isActive && !isChildActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-neutral-200/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                )}
                {IconComponent && <IconComponent isActive={isActive || isChildActive} />}
                <span className="text-sm flex-1">{t(item.labelKey)}</span>
                {(isActive || isChildActive) && !hasChildren && (
                  <div className="w-2 h-2 rounded-full bg-primary-500 shadow-md animate-pulse" />
                )}
                {hasChildren && (
                  <svg className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} ${isActive || isChildActive ? 'text-primary-500' : 'text-neutral-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </Link>

              {hasChildren && isExpanded && (
                <div className="ml-8 mt-1 space-y-1 border-l-2 border-neutral-200 pl-2">
                  {visibleChildren!.map((child) => {
                    const isChildItemActive = location.pathname === child.href;
                    const ChildIcon = iconMap[child.icon];
                    return (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={onNavClick}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium ${isChildItemActive ? 'bg-primary-50 text-primary-700' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800'}`}
                      >
                        {ChildIcon && <ChildIcon isActive={isChildItemActive} />}
                        <span>{t(child.labelKey)}</span>
                        {isChildItemActive && <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse ml-auto" />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-neutral-200 p-4 space-y-3 bg-gradient-to-t from-white via-neutral-50 to-transparent">
        <div className="flex items-center gap-3 px-2 group cursor-pointer transition-transform duration-300 hover:scale-105">
          {user?.profilePictureUrl ? (
            <img src={user.profilePictureUrl} alt={user?.displayName || 'User'} className="w-10 h-10 rounded-full object-cover shadow-lg group-hover:shadow-xl transition-shadow duration-300" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:shadow-xl transition-shadow duration-300">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-neutral-900 truncate">{user?.displayName || t('common.user')}</p>
            <p className="text-xs text-neutral-500">{t(`roles.${user?.role}`, { defaultValue: user?.role || t('roles.member') })}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-gradient-to-r hover:from-accent-red/15 hover:to-accent-red/5 hover:text-accent-red hover:border-accent-red/40 rounded-lg transition-all duration-300 font-medium border border-neutral-200 hover:border-accent-red/40 group shadow-sm hover:shadow-md"
        >
          <IconLogout isActive={false} />
          <span>{t('header.logout')}</span>
        </button>
      </div>
    </>
  );
}
